<template>
  <d-container
    fluid
    class="main-content-container px-0 px-md-2 px-lg-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        sm="12"
        md="9"
        lg="9"
        class="text-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Zamówienia</span>
        <h3 class="page-title">Lista zamówień</h3>
      </d-col>
      <d-col
        col
        sm="12"
        md="3"
        lg="3"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="$router.push('/zamowienia/nowy')">
          <i class="material-icons">add</i>
          Dodaj zamówienie
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="loaded"
      style="width: 100%"
      class="d-flex ml-0 mb-3 page-filters orders-page-filters">
      <validation-observer
        ref="orders-filters-form"
        tag="form"
        style="width: 100%"
        class="d-flex mb-2">
        <d-input
          placeholder="Klient, Adres dostawy"
          v-model="filters.search"
          class="mr-1 mb-2"
          style="min-width: 200px; max-width: 50%;" />

        <date-range-picker
          ref="picker"
          :locale-data="{
            'firstDay': 1,
            'format': 'DD-MM-YYYY',
            'daysOfWeek': ['Ndz', 'Pon', 'Wto', 'Śro', 'Czw', 'Ptk', 'Sob'],
            'monthNames': ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
          }"
          class="mr-1 mb-2"
          style="min-width: 240px;"
          :singleDatePicker="false"
          :timePicker="false"
          :showWeekNumbers="false"
          :showDropdowns="false"
          :autoApply="true"
          v-model="dateRange"
          :ranges="false"
          :linkedCalendars="true">
          <template v-slot:input="picker" style="min-width: 350px; max-width: 50%;">
            {{ picker.startDate | dateStart }} - {{ picker.endDate | dateEnd }}
          </template>
        </date-range-picker>

        <v-select
          v-model="filters.customer"
          class="mr-1 mb-2 customers-select"
          label="name"
          placeholder="Kto złożył zamówienie"
          ref="customers-filter"
          style="width: 360px; min-width: 250px; max-width: 30%;"
          :disabled="!customersLoaded"
          :filter="filterCustomers"
          :options="limitedCustomers"
          :searchable="true">
          <template slot="no-options">
            <template v-if="!customersEmptyResults">Trwa wyszukiwanie&hellip;</template>
            <template v-else>Brak klientów pasujących do podanej frazy&hellip;</template>
          </template>
          <template
            slot="option"
            slot-scope="option">
            <div class="d-center">
              {{ option.invoice_company }}
            </div>
          </template>
          <template
            slot="selected-option"
            slot-scope="option">
            <div class="selected d-center">
              {{ option.invoice_company }}
            </div>
          </template>
        </v-select>

        <v-select
          style="width: 360px; min-width: 180px; max-width: 30%;"
          class="mr-3 mb-2 status-select"
          :options="orderStatusOptions"
          v-model="filters.orderStatus"
          :reduce="status => status.status_code"
          placeholder="Status zamówienia"
          :searchable="false"
          label="label">
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-md-auto mr-1 mb-2"
          @click.prevent="validateFiltersForm">
          Szukaj
        </d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap mb-2"
          @click.prevent="clearFilters">
          Resetuj filtry
        </d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0 align-middle">Adres dostawy</th>
            <th scope="col" class="border-0 align-middle text-center">Data złożenia</th>
            <th scope="col" class="border-0 align-middle text-center">Koszt</th>
            <th scope="col" class="border-0 align-middle text-center">Ilość produktów</th>
            <th scope="col" class="border-0 align-middle text-center">Status</th>
            <th scope="col" class="border-0 align-middle text-center" style="width: 210px;">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr v-for="(order, index) of orders" :key="'order-' + index">
            <td class="align-middle py-lg-3 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Adres dostawy</label>
              {{ order.invoice_company }}<br>
              <template v-if="order.customer_name || order.customer_surname">
                <small>{{ order.customer_name }} {{ order.customer_surname }}</small><br>
              </template>
              <small>
                {{ order.address_street }}, {{ order.address_building_number }}, {{ order.address_house_number }}<br>
                {{ order.address_post_code }}, {{ order.address_city }}
              </small>
            </td>
            <td class="align-middle text-center py-lg-3 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Data złożenia</label>
              {{ order.created_at | formatDate }}
            </td>
            <td class="align-middle text-center py-lg-3 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Koszt</label>
              {{ roundProperly(order.price) }} zł
            </td>
            <td class="align-middle text-center py-lg-3 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Ilość produktów</label>
              {{ order.products_count }}
            </td>
            <td class="align-middle text-center py-lg-3 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Status</label>
              <d-badge
                :theme="orderStatuses[order.status].style"
                :outline="orderStatuses[order.status].outline"
                @click.native.prevent="getIsCustomerInDebt(order.customer_id) && !isAdmin ? () => {} : $bus.$emit('show-order-status-change-popup', order)"
                :style="getIsCustomerInDebt(order.customer_id) && !isAdmin ? 'cursor: not-allowed; opacity: 0.8' : 'cursor: pointer;'">
                {{ orderStatuses[order.status].label }}
              </d-badge>
              <br>
              <small>
                Ostatnia zmiana:<br>
                <strong>{{ order.status_at }}</strong>
              </small>
              <template v-if="getIsCustomerInDebt(order.customer_id)">
                <br>
                <small class="text-danger">
                  Klient zadłużony
                </small>
              </template>
            </td>

            <td class="text-center align-middle pt-0">
              <d-button
                theme="primary"
                @click.native="$router.push('/zamowienia/edycja/' + order.id)"
                class="mx-2 mt-2"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="warning"
                @click.native="$router.push({ name: 'Duplikowanie zamówienia', params: { existingOrderID: order.id }})"
                class="mx-2 mt-2"
                title="Duplikuj">
                <i class="material-icons">content_copy</i>
              </d-button>
              <d-button
                v-if="checkIsOrderRemovable(order.status)"
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeOrder(order.id)"
                title="Usuń"
                class="mx-2 mt-2">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="orders.length === 0">
            <td colspan="6" class="text-center">
              Brak zamówień do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert v-if="loadError" show theme="warning">
        Wczytywanie danych nie powiodło się.
        <a
          href="javascript:window.location.reload();"
          class="alert-link">
          Odśwież stronę
        </a>
        aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination :meta-data="paginationMetaData" />
    <order-status-change-popup />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import OrderStatuses from './../data/order-statuses.js';
import OrderPhases from './../data/order-phases.js';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import CustomDecimals from './../utils/CustomDecimals.js';
import OrderStatusChangePopup from '@/components/popups/OrderStatusChangePopup.vue';

export default {
  name: 'orders',
  mixins: [HasFilters, HasPagination],
  components: {
    'pagination': Pagination,
    'date-range-picker': DateRangePicker,
    'order-status-change-popup': OrderStatusChangePopup,
  },
  filters: {
    formatDate (input) {
      if (!input) {
        return '-';
      }

      let dateObject = new Date(input);

      return [
        dateObject.getFullYear(),
        dateObject.getMonth() + 1,
        dateObject.getDate()
      ]
        .map(n => n < 10 ? '0' + n : n)
        .join('-') + ' ' + [
        dateObject.getHours(),
        dateObject.getMinutes()
      ]
        .map(n => n < 10 ? '0' + n : n)
        .join(':');
    },
    dateStart (input) {
      if (input === null) {
        return ' szukaj od ';
      }

      return [
        input.getFullYear(),
        input.getMonth() + 1,
        input.getDate()
      ]
        .map(n => n < 10 ? '0' + n : n)
        .join('-');
    },
    dateEnd (input) {
      if (input === null) {
        return ' do ';
      }

      return [
        input.getFullYear(),
        input.getMonth() + 1,
        input.getDate()
      ]
        .map(n => n < 10 ? '0' + n : n)
        .join('-');
    }
  },
  computed: {
    orderStatuses () {
      return OrderStatuses;
    },
    orderStatusOptions () {
      let orderStatusesArr = [];
      let keys = Object.keys(OrderStatuses)
      for (let i = 0; i < keys.length; i++) {
        let orderStatus = OrderStatuses[keys[i]];
        orderStatus['status_code'] = keys[i];
        orderStatusesArr.push(orderStatus);
      }
      return orderStatusesArr;
    },
    limitedCustomers () {
      let options = this.allCustomers;

      if (this.$refs['customers-filter'] && this.$refs['customers-filter'].search.length < 1) {
        return options.slice(0, 100);
      }

      return options;
    },
    isAdmin () {
      return this.$store.getters['getUserType'] === 'ADMIN';
    }
  },
  watch: {
    'dateRange.startDate': function (newValue) {
      if (newValue === null) {
        this.filters.dateFrom = null;
      }

      this.filters.dateFrom = this.prepareDateString(newValue);
    },
    'dateRange.endDate': function (newValue) {
      if (newValue === null) {
        this.filters.dateTo = null;
      }

      this.filters.dateTo = this.prepareDateString(newValue);
    }
  },
  data() {
    return {
      orders: [],
      allCustomers: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      customersEmptyResults: false,
      customersLoaded: false,
      filters: {
        search: '',
        orderStatus: null,
        customer: null,
        dateFrom: null,
        dateTo: null
      },
      dateRange: {
        startDate: null,
        endDate: null
      }
    };
  },
  mounted() {
    this.$bus.$on('reload-orders', this.loadFilteredData);
    this.loadFilteredData();
    this.loadAdditionalData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData() {
      let where = {};

      if (this.filters.search) {
        where.search = this.filters.search;
      }

      if (this.filters.orderStatus) {
        where.status = this.filters.orderStatus;
      }

      if (this.filters.customer && this.filters.customer.id) {
        where.customer_id = this.filters.customer.id;
      }

      if (this.filters.dateFrom) {
        where.date_from = this.filters.dateFrom;
      }

      if (this.filters.dateTo) {
        where.date_to = this.filters.dateTo;
      }

      ListUtils.loadItemsData(this, {
        method: 'get',
        endpoint: '/api/orders/items?page=' + this.currentPage,
        listField: 'orders',
        pagination: true,
        params: {
          where
        },
      });
    },
    clearFilters() {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        orderStatus: null,
        customer: null,
        dateFrom: null,
        dateTo: null
      };

      this.dateRange.startDate = null;
      this.dateRange.endDate = null;
      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm() {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(
        this.$refs['orders-filters-form'],
        this.debouncedLoadFilteredData
      );
    },
    removeOrder(orderID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/api/orders/delete/',
        id: orderID,
        successTitle: 'Usunięto zaówienie',
        successText: 'Wybrane zamówienie zostało usunięte',
        confirmTitle: 'Czy na pewno chcesz usunąć to zamówienie?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie zamówienia nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        },
      });
    },
    prepareDateString (input) {
      if (input === null) {
        return null;
      }
      return [
        input.getFullYear(),
        input.getMonth() + 1,
        input.getDate()
      ]
        .map(n => n < 10 ? '0' + n : n)
        .join('-');
    },
    loadAdditionalData() {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/contacts/all-items',
        method: 'get',
        outputKey: 'allCustomers',
        loadedKey: 'customersLoaded',
        errorKey: 'loadError'
      });
    },
    filterCustomers (options, search) {
      let results = options.filter(item => (item.invoice_company).toLocaleLowerCase('pl').indexOf(search.toLocaleLowerCase('pl')) > -1).slice(0, 100);
      if (results.length) {
        this.customersEmptyResults = false;
      } else {
        this.customersEmptyResults = true;
      }
      return results;
    },
    roundProperly (value, decimals = 2) {
      return CustomDecimals.roundProperly(value, decimals);
    },
    checkIsOrderRemovable (orderStatus) {
      return OrderPhases.writable.indexOf(orderStatus) > -1;
    },
    getIsCustomerInDebt(customerID) {
      let currentCustomer = this.allCustomers.find(item => item.id === customerID);

      if (!currentCustomer) {
        return false;
      }

      return currentCustomer.debt_amount > 0;
    }
  },
  beforeDestroy () {
    this.$bus.$on('reload-orders', this.loadFilteredData);
  }
};
</script>

<style lang="scss">

@media screen and (max-width: 1430px) {
  .orders-page-filters form {
    flex-wrap: wrap;

    input {
      margin-right: 0.25rem !important;
      width: 50%;
    }

    .vue-daterange-picker {
      margin-right: 0!important;
      width: calc(50% - 10px);
    }
  }
}

@media screen and (max-width: 930px) {
  .orders-page-filters form {
    .customers-select {
      margin-right: 0!important;
      max-width: unset!important;
      width: 100%!important;
    }

    .status-select {
      margin-right: 0!important;
      max-width: unset!important;
      width: 100%!important;
    }
  }
}

@media screen and (max-width: 500px) {
  .orders-page-filters form {
    input {
      margin-right: 0!important;
      max-width: unset!important;
      width: 100%;
    }

    .vue-daterange-picker {
      max-width: unset!important;
      width: 100%;
    }

    .customers-select {
      min-width: unset!important;
    }

    .status-select {
      min-width: unset!important;
    }
  }
}

</style>
